import _ from 'lodash';

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { onEventPreventPropagation } from '@/core/onEnterKeypress.util';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { SearchWidget } from '@/search/SearchWidget.organism';
import { isAsset } from '@/utilities/utilities';
import { Checkbox } from '@seeqdev/qomponents';
import type { SearchWidgetSharedProps } from '@/search/SearchWidget.organism';
import type { SelectedSearchItem } from '@/search/search.types';

export interface SelectItemSearchWidgetProps extends SearchWidgetSharedProps {
  onSelect: (item: SelectedSearchItem) => void;
  iconPartialTooltipKey: string;
  selectedItems?: SelectedSearchItem[];
  validateItem: (item: SelectedSearchItem) => Promise<boolean>;
  modalId?: string;
  isMultiple?: boolean;
  showRecentlyAccessed?: boolean;
  restrictExploration?: boolean;
  initialItemTable?: SelectedSearchItem;
  initialHierarchy?: SelectedSearchItem;
}

export const SelectItemSearchWidget = ({
  onSelect,
  iconPartialTooltipKey,
  selectedItems,
  validateItem,
  scopeIds,
  excludeGloballyScoped = false,
  modalId,
  searchTypes,
  isMultiple = false,
  selectAllCallback = undefined,
  areAllSelected = () => false,
  showPinnedAssets = false,
  showOnlyScopedAssets = false,
  showOnlyConditions = false,
  showRecentlyAccessed = false,
  overrideWorkbookId = undefined,
  filterByWorkbook,
  itemTypeToShowInOverview,
  overviewLabel,
  showCreateNewItem = false,
  onCreateNewItem = _.noop,
  createNewItemMessage = '',
  createNewItemLabel = '',
  doNotDisableOnEdit = false,
  restrictExploration = false,
  initialItemTable = undefined,
  initialHierarchy = undefined,
}: SelectItemSearchWidgetProps) => {
  const { t } = useTranslation();

  const isSelectingAssets = searchTypes.includes(SeeqNames.Types.Asset);

  const searchResultIcons = useCallback(
    async (item: SelectedSearchItem): Promise<React.ReactNode> => {
      const onlySearchingAssets = isSelectingAssets && searchTypes.length === 1;
      if (!isAsset(item) && onlySearchingAssets) {
        return Promise.resolve(null);
      }

      const isValid = await validateItem(item);
      const isSelected = selectedItems?.some((selectedItem) => selectedItem.id === item.id);

      return isValid ? (
        <Checkbox
          type={isMultiple ? 'checkbox' : 'radio'}
          testId="itemSelect"
          extraClassNames="ml5"
          tooltip={t(`${iconPartialTooltipKey}.ASSET_TOOLTIP`)}
          tooltipPlacement="top"
          checked={isSelected}
          onClick={onEventPreventPropagation(() => onSelect(item as SelectedSearchItem))}
        />
      ) : (
        <Checkbox
          type={isMultiple ? 'checkbox' : 'radio'}
          testId="itemSelectInvalid"
          extraClassNames="disabled ml5"
          tooltip={t(`${iconPartialTooltipKey}.ASSET_TOOLTIP_INVALID`)}
          onClick={onEventPreventPropagation(_.noop)}
          disabled={true}
        />
      );
    },
    [selectedItems, validateItem, iconPartialTooltipKey, onSelect],
  );

  return (
    <SearchWidget
      isSelectingAsset={isSelectingAssets}
      iconClasses="sq-fairly-dark-gray"
      pane="modal"
      searchTypes={searchTypes}
      searchResultIcons={searchResultIcons}
      allowAssetReplacement={false}
      showOnlyResults={
        !showRecentlyAccessed && !(searchTypes.includes(SeeqNames.Types.Asset) && searchTypes.length > 2)
      }
      showPinnedAssets={showPinnedAssets}
      scopeIds={scopeIds}
      excludeGloballyScoped={excludeGloballyScoped}
      modalId={modalId}
      predefinedSearchTypes={searchTypes}
      onSelectItem={onSelect}
      selectAllCallback={selectAllCallback}
      areAllSelected={areAllSelected}
      showOnlyScopedAssets={showOnlyScopedAssets}
      showOnlyConditions={showOnlyConditions}
      overrideWorkbookId={overrideWorkbookId}
      filterByWorkbook={filterByWorkbook}
      itemTypeToShowInOverview={itemTypeToShowInOverview}
      overviewLabel={overviewLabel}
      showCreateNewItem={showCreateNewItem}
      onCreateNewItem={onCreateNewItem}
      createNewItemMessage={createNewItemMessage}
      createNewItemLabel={createNewItemLabel}
      doNotDisableOnEdit={doNotDisableOnEdit}
      restrictExploration={restrictExploration}
      initialItemTable={initialItemTable}
      initialHierarchy={initialHierarchy}
    />
  );
};
